import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/account";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const AuthModal = ({ open, onOpenChange, defaultMode = "register" }) => {
  const { setUser } = useAuth();
  const [mode, setMode] = useState(defaultMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => setMode(defaultMode), [defaultMode, open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const path = mode === "register" ? "/auth/register" : "/auth/login";
      const payload = mode === "register" ? { name, email, password } : { email, password };
      const { data } = await api.post(path, payload);
      setUser(data.user);
      toast.success(mode === "register" ? "Welcome to 1of1." : "Welcome back.");
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="auth-modal"
        className="sm:max-w-[440px] rounded-none border border-white/15 bg-[#0a0a0a] p-0 text-[#f5f5f0]"
      >
        <VisuallyHidden>
          <DialogTitle>{mode === "register" ? "Request access" : "Sign in"}</DialogTitle>
          <DialogDescription>Access the 1of1 collection.</DialogDescription>
        </VisuallyHidden>
        <div className="p-10">
          <p className="font-sans-lux text-[10px] uppercase tracking-[0.4em] text-gold">1of1</p>
          <h2 className="font-serif-lux mt-4 text-4xl font-light leading-none">
            {mode === "register" ? "Request access" : "Sign in"}
          </h2>
          <p className="mt-3 text-sm font-light text-[#a1a1aa]">
            {mode === "register"
              ? "Join the register of collectors acquiring the singular."
              : "Return to your private collection."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <input
                data-testid="auth-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                required
                className="w-full rounded-none border-b border-white/20 bg-transparent px-1 py-3 text-sm outline-none placeholder:text-[#52525b] focus:border-gold"
              />
            )}
            <input
              data-testid="auth-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              required
              className="w-full rounded-none border-b border-white/20 bg-transparent px-1 py-3 text-sm outline-none placeholder:text-[#52525b] focus:border-gold"
            />
            <input
              data-testid="auth-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              className="w-full rounded-none border-b border-white/20 bg-transparent px-1 py-3 text-sm outline-none placeholder:text-[#52525b] focus:border-gold"
            />
            {error && (
              <p data-testid="auth-error" className="text-sm font-light text-red-400">
                {error}
              </p>
            )}
            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={busy}
              className="hover-invert mt-2 w-full rounded-none border border-[#f5f5f0] bg-[#f5f5f0] px-6 py-4 text-[11px] uppercase tracking-[0.3em] text-black hover:bg-transparent hover:text-[#f5f5f0] disabled:opacity-50"
            >
              {busy ? "Please wait" : mode === "register" ? "Create account" : "Sign in"}
            </button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <span className="h-px flex-1 bg-white/10" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#52525b]">or</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <button
            data-testid="auth-google-btn"
            onClick={googleLogin}
            className="hover-invert w-full rounded-none border border-white/25 px-6 py-4 text-[11px] uppercase tracking-[0.3em] text-[#f5f5f0] hover:bg-[#f5f5f0] hover:text-black"
          >
            Continue with Google
          </button>

          <p className="mt-8 text-center text-xs font-light text-[#a1a1aa]">
            {mode === "register" ? "Already a member?" : "New to 1of1?"}{" "}
            <button
              data-testid="auth-toggle-mode"
              onClick={() => setMode(mode === "register" ? "login" : "register")}
              className="text-gold underline underline-offset-4"
            >
              {mode === "register" ? "Sign in" : "Request access"}
            </button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
